// src/store/slices/socketSlice.js

import { createSlice, createAction, createAsyncThunk } from '@reduxjs/toolkit';

//  Global logout action that can be used across slices
export const logoutUser = createAction('auth/logout');

//  Enhanced async thunk for socket reconnection
export const attemptReconnect = createAsyncThunk(
  'socket/attemptReconnect',
  async (_, { getState, dispatch }) => {
    const { socket } = getState();
    const { getSocket, reconnectSocket } = await import('../../services/socket');
    try {
      if (!getSocket()) {
        throw new Error('Socket instance not available');
      }
      await reconnectSocket();
      return { success: true, timestamp: new Date().toISOString() };
    } catch (error) {
      throw new Error(`Reconnection failed: ${error.message}`);
    }
  }
);

//  Enhanced async thunk for sending FIAS messages
export const sendFiasMessageAsync = createAsyncThunk(
  'socket/sendFiasMessage',
  async (messageData, { getState, rejectWithValue }) => {
    const { socket } = getState();
    if (!socket.connected) {
      return rejectWithValue('Socket not connected');
    }
    try {
      const { sendFiasMessage } = await import('../../services/socket');
      const result = await sendFiasMessage(messageData);
      return {
        ...messageData,
        result,
        timestamp: new Date().toISOString(),
        status: 'sent'
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  // ===================================
  // CONNECTION STATE
  // ===================================
  connected: false,
  connecting: false,
  reconnectAttempts: 0,
  maxReconnectAttempts: 10,
  lastError: null,
  connectionInfo: {
    id: null,
    transport: null,
    connectedAt: null,
    disconnectedAt: null,
    serverUrl: null,
    latency: null,
    reconnectAttempts: 0
  },

  // ===================================
  // FIAS MESSAGES & COMMUNICATIONS
  // ===================================
  fiasMessages: [], // All FIAS protocol messages
  fiasConnections: [], // Active FIAS connections
  fiasStats: {
    totalMessages: 0,
    messagesReceived: 0,
    messagesSent: 0,
    lastMessageTime: null,
    connectionUptime: 0,
    errorCount: 0,
    checkIns: 0,
    checkOuts: 0,
    linkMessages: 0
  },

  // ===================================
  // DEVICE STATUS & MONITORING
  // ===================================
  deviceStatuses: {}, // Real-time device statuses
  deviceEvents: [], // Device status change events
  lastDeviceUpdate: null,

  // ===================================
  // ROOM STATUS & OPERATIONS
  // ===================================
  roomStatuses: {}, // Real-time room statuses
  roomEvents: [], // Room status change events
  lastRoomUpdate: null,

  // ===================================
  // VOUCHER & INTEGRATION EVENTS
  // ===================================
  voucherEvents: [], // Voucher application/usage events
  integrationStatuses: {
    fias: { enabled: false, connected: false, lastUpdate: null },
    ruijie: { enabled: false, connected: false, lastUpdate: null }
  },
  integrationEvents: [], // Integration status changes

  // ===================================
  // SYSTEM NOTIFICATIONS
  // ===================================
  notifications: [], // System notifications
  alerts: [], // Critical system alerts
  systemHealth: {
    overall: 'unknown',
    database: 'unknown',
    integrations: 'unknown',
    lastCheck: null
  },

  // ===================================
  // FILTERING & SEARCH
  // ===================================
  messageFilters: {
    recordTypes: [], // Filter by FIAS record types (GI, GO, etc.)
    directions: ['incoming', 'outgoing'], // Message directions
    searchTerm: '', // Text search
    dateRange: {
      start: null,
      end: null
    },
    connectionId: null, // Filter by connection ID
    severity: [], // Filter by message severity
    showProcessedOnly: false
  },

  // ===================================
  // UI STATE & PREFERENCES
  // ===================================
  uiState: {
    autoScroll: true, // Auto-scroll to new messages
    soundEnabled: true, // Play sounds for notifications
    showTimestamps: true, // Show detailed timestamps
    compactMode: false, // Compact message display
    selectedTab: 'all', // Active monitoring tab
    expandedSections: {
      fias: true,
      devices: true,
      rooms: true,
      integrations: true
    }
  },

  // ===================================
  // PERFORMANCE & STATISTICS
  // ===================================
  performance: {
    messageProcessingTime: [],
    averageLatency: 0,
    reconnectHistory: [],
    errorHistory: [],
    uptime: {
      start: null,
      total: 0,
      current: 0
    }
  },

  // ===================================
  // REAL-TIME DATA SUBSCRIPTIONS
  // ===================================
  subscriptions: {
    branches: [], // Subscribed branch IDs
    devices: [], // Subscribed device IDs
    integrations: [], // Subscribed integration types
    monitoring: {
      enabled: false,
      interval: 30000,
      lastUpdate: null
    }
  },

  // ===================================
  // SETTINGS & CONFIGURATION
  // ===================================
  settings: {
    autoReconnect: true,
    reconnectInterval: 5000,
    maxReconnectAttempts: 10,
    heartbeatInterval: 30000,
    messageRetention: 1000, // Max messages to keep
    notificationRetention: 100, // Max notifications to keep
    enableDebugMode: false,
    soundSettings: {
      enabled: true,
      volume: 0.5,
      notifications: true,
      alerts: true,
      fiasMessages: false
    }
  }
};

const socketSlice = createSlice({
  name: 'socket',
  initialState,
  reducers: {
    // ===================================
    // CONNECTION MANAGEMENT
    // ===================================
    setConnected: (state, action) => {
      const wasConnected = state.connected;
      state.connected = action.payload;
      if (action.payload) {
        // Connection established
        state.connecting = false;
        state.reconnectAttempts = 0;
        state.lastError = null;
        state.connectionInfo.connectedAt = new Date().toISOString();
        state.performance.uptime.start = new Date().toISOString();
        // Reset error counts
        state.fiasStats.errorCount = 0;
        // Add success notification
        if (!wasConnected) {
          state.notifications.unshift({
            id: `conn_${Date.now()}`,
            type: 'success',
            message: 'Connected to server successfully',
            timestamp: new Date().toISOString(),
            read: false,
            category: 'connection'
          });
        }
      } else {
        // Connection lost
        state.connecting = false;
        state.connectionInfo.disconnectedAt = new Date().toISOString();
        // Update uptime
        if (state.performance.uptime.start) {
          const disconnectTime = Date.now();
          const connectTime = new Date(state.performance.uptime.start).getTime();
          const sessionUptime = disconnectTime - connectTime;
          state.performance.uptime.total += sessionUptime;
        }
      }
    },

    setConnecting: (state, action) => {
      state.connecting = action.payload;
      if (action.payload) {
        state.lastError = null;
      }
    },

    setReconnectAttempts: (state, action) => {
      state.reconnectAttempts = action.payload;
      state.connectionInfo.reconnectAttempts = action.payload;
      // Track reconnect attempts
      if (action.payload > 0) {
        state.performance.reconnectHistory.push({
          attempt: action.payload,
          timestamp: new Date().toISOString()
        });
        // Keep only last 50 reconnect attempts
        if (state.performance.reconnectHistory.length > 50) {
          state.performance.reconnectHistory = state.performance.reconnectHistory.slice(-50);
        }
      }
    },

    setLastError: (state, action) => {
      state.lastError = action.payload;
      if (action.payload) {
        state.connecting = false;
        state.connected = false;
        // Track error
        state.performance.errorHistory.push({
          error: action.payload,
          timestamp: new Date().toISOString(),
          reconnectAttempt: state.reconnectAttempts
        });
        // Keep only last 100 errors
        if (state.performance.errorHistory.length > 100) {
          state.performance.errorHistory = state.performance.errorHistory.slice(-100);
        }
        // Add error notification for critical errors
        if (!action.payload.includes('timeout') && !action.payload.includes('reconnect')) {
          state.notifications.unshift({
            id: `error_${Date.now()}`,
            type: 'error',
            message: `Connection error: ${action.payload}`,
            timestamp: new Date().toISOString(),
            read: false,
            category: 'connection'
          });
        }
      }
    },

    setConnectionInfo: (state, action) => {
      state.connectionInfo = { ...state.connectionInfo, ...action.payload };
    },

    updateLatency: (state, action) => {
      const newLatency = action.payload;
      state.connectionInfo.latency = newLatency;
      // Calculate rolling average latency
      state.performance.messageProcessingTime.push(newLatency);
      if (state.performance.messageProcessingTime.length > 100) {
        state.performance.messageProcessingTime = state.performance.messageProcessingTime.slice(-100);
      }
      state.performance.averageLatency =
        state.performance.messageProcessingTime.reduce((a, b) => a + b, 0) /
        state.performance.messageProcessingTime.length;
    },

    //  MISSING ACTIONS - ADDED FOR BACKWARDS COMPATIBILITY
    updateStats: (state, action) => {
      // Map old stats format to new structure
      const { messagesReceived, messagesSent, totalMessages, lastActivity, ...otherStats } = action.payload;
      if (messagesReceived !== undefined) {
        state.fiasStats.messagesReceived = messagesReceived;
      }
      if (messagesSent !== undefined) {
        state.fiasStats.messagesSent = messagesSent;
      }
      if (totalMessages !== undefined) {
        state.fiasStats.totalMessages = totalMessages;
      }
      if (lastActivity !== undefined) {
        state.fiasStats.lastMessageTime = lastActivity;
      }
      // Update any additional stats
      Object.keys(otherStats).forEach(key => {
        if (state.fiasStats.hasOwnProperty(key)) {
          state.fiasStats[key] = otherStats[key];
        }
      });
    },

    incrementMessageCount: (state, action) => {
      const { direction } = action.payload;
      if (direction === 'incoming') {
        state.fiasStats.messagesReceived += 1;
      } else if (direction === 'outgoing') {
        state.fiasStats.messagesSent += 1;
      }
      state.fiasStats.totalMessages += 1;
      state.fiasStats.lastMessageTime = new Date().toISOString();
    },

    // ===================================
    // FIAS MESSAGE MANAGEMENT
    // ===================================
    addFiasMessage: (state, action) => {
      const message = {
        ...action.payload,
        id: action.payload.messageId || `fias_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: action.payload.timestamp || new Date().toISOString(),
        processed: action.payload.processed || false,
        category: 'fias'
      };

      // Add to messages array
      state.fiasMessages.unshift(message);

      // Update statistics
      state.fiasStats.totalMessages += 1;
      state.fiasStats.lastMessageTime = message.timestamp;

      if (message.direction === 'incoming') {
        state.fiasStats.messagesReceived += 1;
        // Count specific FIAS record types
        const recordId = message.message?.recordId;
        if (recordId === 'GI') state.fiasStats.checkIns += 1;
        else if (recordId === 'GO') state.fiasStats.checkOuts += 1;
        else if (['LS', 'LA', 'LD', 'LR', 'LE'].includes(recordId)) {
          state.fiasStats.linkMessages += 1;
        }
      } else if (message.direction === 'outgoing') {
        state.fiasStats.messagesSent += 1;
      }

      // Keep only configured number of messages
      if (state.fiasMessages.length > state.settings.messageRetention) {
        state.fiasMessages = state.fiasMessages.slice(0, state.settings.messageRetention);
      }

      // Add notification for important FIAS messages
      const recordId = message.message?.recordId;
      if (['GI', 'GO', 'GC', 'LE'].includes(recordId)) {
        const messageTypes = {
          'GI': 'Guest Check-in',
          'GO': 'Guest Check-out',
          'GC': 'Guest Change',
          'LE': 'Link End'
        };
        state.notifications.unshift({
          id: `fias_${Date.now()}`,
          type: 'info',
          message: `FIAS: ${messageTypes[recordId]} - Room ${message.message?.fields?.RN || 'Unknown'}`,
          timestamp: message.timestamp,
          read: false,
          category: 'fias',
          data: { recordId, message: message.message }
        });
      }
    },

    updateFiasMessageStatus: (state, action) => {
      const { messageId, status, error } = action.payload;
      const message = state.fiasMessages.find(msg => msg.id === messageId);
      if (message) {
        message.processed = status === 'processed';
        message.status = status;
        if (error) {
          message.error = error;
          state.fiasStats.errorCount += 1;
        }
        message.updatedAt = new Date().toISOString();
      }
    },

    addFiasConnection: (state, action) => {
      const connection = {
        ...action.payload,
        connectedAt: new Date().toISOString(),
        messageCount: 0,
        status: 'connected'
      };
      state.fiasConnections.push(connection);
      state.notifications.unshift({
        id: `fias_conn_${Date.now()}`,
        type: 'success',
        message: `FIAS connection established: ${connection.host}:${connection.port}`,
        timestamp: new Date().toISOString(),
        read: false,
        category: 'fias'
      });
    },

    removeFiasConnection: (state, action) => {
      const connectionId = action.payload;
      state.fiasConnections = state.fiasConnections.filter(conn => conn.id !== connectionId);
      state.notifications.unshift({
        id: `fias_disconn_${Date.now()}`,
        type: 'warning',
        message: `FIAS connection lost: ${connectionId}`,
        timestamp: new Date().toISOString(),
        read: false,
        category: 'fias'
      });
    },

    // ===================================
    // DEVICE STATUS MANAGEMENT
    // ===================================
    updateDeviceStatus: (state, action) => {
      const { deviceId, status, timestamp, ...otherData } = action.payload;
      // Store previous status
      const previousStatus = state.deviceStatuses[deviceId]?.status;
      // Update device status
      state.deviceStatuses[deviceId] = {
        status,
        timestamp: timestamp || new Date().toISOString(),
        previousStatus,
        lastUpdate: new Date().toISOString(),
        ...otherData
      };
      state.lastDeviceUpdate = new Date().toISOString();

      // Add to device events
      state.deviceEvents.unshift({
        id: `device_${Date.now()}`,
        deviceId,
        status,
        previousStatus,
        timestamp: timestamp || new Date().toISOString(),
        ...otherData
      });

      // Keep only last 500 device events
      if (state.deviceEvents.length > 500) {
        state.deviceEvents = state.deviceEvents.slice(0, 500);
      }

      // Add notification for critical status changes
      if (status === 'error' || status === 'offline') {
        state.notifications.unshift({
          id: `device_alert_${Date.now()}`,
          type: status === 'error' ? 'error' : 'warning',
          message: `Device ${otherData.deviceName || deviceId} is ${status}`,
          timestamp: new Date().toISOString(),
          read: false,
          category: 'device',
          data: { deviceId, status, previousStatus }
        });
      } else if (status === 'online' && previousStatus && previousStatus !== 'online') {
        state.notifications.unshift({
          id: `device_recovery_${Date.now()}`,
          type: 'success',
          message: `Device ${otherData.deviceName || deviceId} is back online`,
          timestamp: new Date().toISOString(),
          read: false,
          category: 'device',
          data: { deviceId, status, previousStatus }
        });
      }
    },

    // ===================================
    // ROOM STATUS MANAGEMENT
    // ===================================
    updateRoomStatus: (state, action) => {
      const { roomId, status, guestInfo, timestamp, ...otherData } = action.payload;
      // Store previous status
      const previousStatus = state.roomStatuses[roomId]?.status;
      // Update room status
      state.roomStatuses[roomId] = {
        status,
        guestInfo,
        timestamp: timestamp || new Date().toISOString(),
        previousStatus,
        lastUpdate: new Date().toISOString(),
        ...otherData
      };
      state.lastRoomUpdate = new Date().toISOString();
      // Add to room events
      state.roomEvents.unshift({
        id: `room_${Date.now()}`,
        roomId,
        status,
        previousStatus,
        guestInfo,
        timestamp: timestamp || new Date().toISOString(),
        ...otherData
      });
      // Keep only last 500 room events
      if (state.roomEvents.length > 500) {
        state.roomEvents = state.roomEvents.slice(0, 500);
      }
    },

    // ===================================
    // VOUCHER EVENT MANAGEMENT
    // ===================================
    addVoucherEvent: (state, action) => {
      const event = {
        ...action.payload,
        id: `voucher_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: action.payload.timestamp || new Date().toISOString()
      };
      state.voucherEvents.unshift(event);

      // Keep only last 1000 voucher events
      if (state.voucherEvents.length > 1000) {
        state.voucherEvents = state.voucherEvents.slice(0, 1000);
      }

      // Add notification for voucher events
      const eventTypes = {
        'applied': 'applied successfully',
        'failed': 'application failed',
        'expired': 'expired',
        'activated': 'activated'
      };
      const message = eventTypes[event.action] || event.action;
      state.notifications.unshift({
        id: `voucher_notif_${Date.now()}`,
        type: event.action === 'failed' ? 'error' : 'info',
        message: `Voucher ${event.voucherCode} ${message}${event.roomNumber ? ` - Room ${event.roomNumber}` : ''}`,
        timestamp: event.timestamp,
        read: false,
        category: 'voucher',
        data: event
      });
    },

    // ===================================
    // INTEGRATION STATUS MANAGEMENT
    // ===================================
    updateIntegrationStatus: (state, action) => {
      const { integration, status, enabled, connected, lastUpdate, ...otherData } = action.payload;
      if (state.integrationStatuses[integration]) {
        const previousStatus = state.integrationStatuses[integration];
        state.integrationStatuses[integration] = {
          ...previousStatus,
          status,
          enabled: enabled !== undefined ? enabled : previousStatus.enabled,
          connected: connected !== undefined ? connected : previousStatus.connected,
          lastUpdate: lastUpdate || new Date().toISOString(),
          ...otherData
        };

        // Add integration event
        state.integrationEvents.unshift({
          id: `integration_${Date.now()}`,
          integration,
          status,
          enabled,
          connected,
          timestamp: new Date().toISOString(),
          previousStatus: previousStatus.status,
          ...otherData
        });

        // Keep only last 200 integration events
        if (state.integrationEvents.length > 200) {
          state.integrationEvents = state.integrationEvents.slice(0, 200);
        }

        // Add notification for integration changes
        const statusMessages = {
          enabled: 'enabled',
          disabled: 'disabled',
          connected: 'connected',
          disconnected: 'disconnected',
          error: 'encountered an error',
          configured: 'configured successfully'
        };
        const message = statusMessages[status] || status;
        state.notifications.unshift({
          id: `integration_notif_${Date.now()}`,
          type: status === 'error' ? 'error' : (status === 'disabled' ? 'warning' : 'info'),
          message: `${integration.toUpperCase()} integration ${message}`,
          timestamp: new Date().toISOString(),
          read: false,
          category: 'integration',
          data: { integration, status, enabled, connected }
        });
      }
    },

    // ===================================
    // NOTIFICATION MANAGEMENT
    // ===================================
    addNotification: (state, action) => {
      const notification = {
        ...action.payload,
        id: action.payload.id || `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: action.payload.timestamp || new Date().toISOString(),
        read: false,
        type: action.payload.type || 'info',
        category: action.payload.category || 'system'
      };

      // Add to beginning of notifications array
      state.notifications.unshift(notification);

      // Keep only configured number of notifications
      if (state.notifications.length > state.settings.notificationRetention) {
        state.notifications = state.notifications.slice(0, state.settings.notificationRetention);
      }
    },

    addAlert: (state, action) => {
      const alert = {
        ...action.payload,
        id: action.payload.id || `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: action.payload.timestamp || new Date().toISOString(),
        acknowledged: false,
        severity: action.payload.severity || 'high'
      };
      state.alerts.unshift(alert);

      // Keep only last 50 alerts
      if (state.alerts.length > 50) {
        state.alerts = state.alerts.slice(0, 50);
      }
    },

    markNotificationRead: (state, action) => {
      const notification = state.notifications.find(n => n.id === action.payload);
      if (notification) {
        notification.read = true;
        notification.readAt = new Date().toISOString();
      }
    },

    markAllNotificationsRead: (state) => {
      const timestamp = new Date().toISOString();
      state.notifications.forEach(notification => {
        if (!notification.read) {
          notification.read = true;
          notification.readAt = timestamp;
        }
      });
    },

    acknowledgeAlert: (state, action) => {
      const alert = state.alerts.find(a => a.id === action.payload);
      if (alert) {
        alert.acknowledged = true;
        alert.acknowledgedAt = new Date().toISOString();
      }
    },

    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },

    // ===================================
    // SYSTEM HEALTH MANAGEMENT
    // ===================================
    updateSystemHealth: (state, action) => {
      state.systemHealth = {
        ...state.systemHealth,
        ...action.payload,
        lastCheck: new Date().toISOString()
      };

      // Add alert for critical system health issues
      if (action.payload.overall === 'critical') {
        state.alerts.unshift({
          id: `health_critical_${Date.now()}`,
          type: 'critical',
          severity: 'critical',
          message: 'System health is critical',
          timestamp: new Date().toISOString(),
          acknowledged: false,
          data: action.payload
        });
      }
    },

    // ===================================
    // SUBSCRIPTION MANAGEMENT
    // ===================================
    addSubscription: (state, action) => {
      const { type, id } = action.payload;
      if (type === 'branch' && !state.subscriptions.branches.includes(id)) {
        state.subscriptions.branches.push(id);
      } else if (type === 'device' && !state.subscriptions.devices.includes(id)) {
        state.subscriptions.devices.push(id);
      } else if (type === 'integration' && !state.subscriptions.integrations.includes(id)) {
        state.subscriptions.integrations.push(id);
      }
    },

    removeSubscription: (state, action) => {
      const { type, id } = action.payload;
      if (type === 'branch') {
        state.subscriptions.branches = state.subscriptions.branches.filter(b => b !== id);
      } else if (type === 'device') {
        state.subscriptions.devices = state.subscriptions.devices.filter(d => d !== id);
      } else if (type === 'integration') {
        state.subscriptions.integrations = state.subscriptions.integrations.filter(i => i !== id);
      }
    },

    updateMonitoringSettings: (state, action) => {
      state.subscriptions.monitoring = {
        ...state.subscriptions.monitoring,
        ...action.payload,
        lastUpdate: new Date().toISOString()
      };
    },

    // ===================================
    // FILTER & SEARCH MANAGEMENT
    // ===================================
    setMessageFilters: (state, action) => {
      state.messageFilters = { ...state.messageFilters, ...action.payload };
    },

    resetMessageFilters: (state) => {
      state.messageFilters = {
        recordTypes: [],
        directions: ['incoming', 'outgoing'],
        searchTerm: '',
        dateRange: { start: null, end: null },
        connectionId: null,
        severity: [],
        showProcessedOnly: false
      };
    },

    // ===================================
    // UI STATE MANAGEMENT
    // ===================================
    updateUIState: (state, action) => {
      state.uiState = { ...state.uiState, ...action.payload };
    },

    toggleExpandedSection: (state, action) => {
      const section = action.payload;
      state.uiState.expandedSections[section] = !state.uiState.expandedSections[section];
    },

    // ===================================
    // SETTINGS MANAGEMENT
    // ===================================
    updateSettings: (state, action) => {
      state.settings = { ...state.settings, ...action.payload };
    },

    updateSoundSettings: (state, action) => {
      state.settings.soundSettings = { ...state.settings.soundSettings, ...action.payload };
    },

    // ===================================
    // CLEANUP & MAINTENANCE
    // ===================================
    clearMessages: (state) => {
      state.fiasMessages = [];
      state.fiasStats = {
        ...state.fiasStats,
        totalMessages: 0,
        messagesReceived: 0,
        messagesSent: 0,
        checkIns: 0,
        checkOuts: 0,
        linkMessages: 0
      };
    },

    clearNotifications: (state) => {
      state.notifications = [];
    },

    clearAlerts: (state) => {
      state.alerts = [];
    },

    clearDeviceEvents: (state) => {
      state.deviceEvents = [];
    },

    clearRoomEvents: (state) => {
      state.roomEvents = [];
    },

    clearVoucherEvents: (state) => {
      state.voucherEvents = [];
    },

    clearOldMessages: (state, action) => {
      const hoursAgo = action.payload || 24;
      const cutoffTime = new Date(Date.now() - hoursAgo * 60 * 60 * 1000).toISOString();
      state.fiasMessages = state.fiasMessages.filter(msg => msg.timestamp > cutoffTime);
      state.deviceEvents = state.deviceEvents.filter(event => event.timestamp > cutoffTime);
      state.roomEvents = state.roomEvents.filter(event => event.timestamp > cutoffTime);
      state.voucherEvents = state.voucherEvents.filter(event => event.timestamp > cutoffTime);
    },

    clearOldNotifications: (state, action) => {
      const hoursAgo = action.payload || 24;
      const cutoffTime = new Date(Date.now() - hoursAgo * 60 * 60 * 1000).toISOString();
      state.notifications = state.notifications.filter(notif => notif.timestamp > cutoffTime);
    },

    // ===================================
    // BULK OPERATIONS
    // ===================================
    addBulkFiasMessages: (state, action) => {
      const messages = action.payload.map(msg => ({
        ...msg,
        id: msg.messageId || `bulk_fias_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: msg.timestamp || new Date().toISOString(),
        processed: msg.processed || false,
        category: 'fias'
      }));
      state.fiasMessages.unshift(...messages);

      // Update stats
      state.fiasStats.totalMessages += messages.length;
      messages.forEach(msg => {
        if (msg.direction === 'incoming') {
          state.fiasStats.messagesReceived += 1;
        } else if (msg.direction === 'outgoing') {
          state.fiasStats.messagesSent += 1;
        }
      });
      state.fiasStats.lastMessageTime = new Date().toISOString();

      // Keep only configured number of messages
      if (state.fiasMessages.length > state.settings.messageRetention) {
        state.fiasMessages = state.fiasMessages.slice(0, state.settings.messageRetention);
      }
    },

    updateBulkDeviceStatuses: (state, action) => {
      const updates = action.payload;
      const timestamp = new Date().toISOString();
      updates.forEach(update => {
        const { deviceId, ...statusData } = update;
        const previousStatus = state.deviceStatuses[deviceId]?.status;
        state.deviceStatuses[deviceId] = {
          ...statusData,
          timestamp,
          previousStatus,
          lastUpdate: timestamp
        };
      });
      state.lastDeviceUpdate = timestamp;
    },

    // ===================================
    // RESET OPERATIONS
    // ===================================
    resetConnection: (state) => {
      state.connected = false;
      state.connecting = false;
      state.reconnectAttempts = 0;
      state.lastError = null;
      state.connectionInfo = {
        id: null,
        transport: null,
        connectedAt: null,
        disconnectedAt: null,
        serverUrl: null,
        latency: null,
        reconnectAttempts: 0
      };
    },

    resetStats: (state) => {
      state.fiasStats = {
        totalMessages: 0,
        messagesReceived: 0,
        messagesSent: 0,
        lastMessageTime: null,
        connectionUptime: 0,
        errorCount: 0,
        checkIns: 0,
        checkOuts: 0,
        linkMessages: 0
      };
      state.performance = {
        messageProcessingTime: [],
        averageLatency: 0,
        reconnectHistory: [],
        errorHistory: [],
        uptime: {
          start: null,
          total: 0,
          current: 0
        }
      };
    },

    resetAll: (state) => {
      Object.assign(state, initialState);
    }
  },

  // ===================================
  // EXTRA REDUCERS (Async Actions)
  // ===================================
  extraReducers: (builder) => {
    builder
      // Handle global logout
      .addCase(logoutUser, () => {
        return initialState;
      })
      // Handle reconnect attempts
      .addCase(attemptReconnect.pending, (state) => {
        state.connecting = true;
        state.lastError = null;
      })
      .addCase(attemptReconnect.fulfilled, (state, action) => {
        state.connecting = false;
        state.lastError = null;
        state.notifications.unshift({
          id: `reconnect_success_${Date.now()}`,
          type: 'success',
          message: 'Successfully reconnected to server',
          timestamp: action.payload.timestamp,
          read: false,
          category: 'connection'
        });
      })
      .addCase(attemptReconnect.rejected, (state, action) => {
        state.connecting = false;
        state.lastError = action.error.message;
        state.notifications.unshift({
          id: `reconnect_failed_${Date.now()}`,
          type: 'error',
          message: `Reconnection failed: ${action.error.message}`,
          timestamp: new Date().toISOString(),
          read: false,
          category: 'connection'
        });
      })
      // Handle FIAS message sending
      .addCase(sendFiasMessageAsync.pending, (state, action) => {
        const tempMessage = {
          id: `temp_${Date.now()}`,
          ...action.meta.arg,
          direction: 'outgoing',
          timestamp: new Date().toISOString(),
          status: 'sending',
          processed: false
        };
        state.fiasMessages.unshift(tempMessage);
      })
      .addCase(sendFiasMessageAsync.fulfilled, (state, action) => {
        // Update the temp message with success status
        const tempMessage = state.fiasMessages.find(msg => msg.status === 'sending');
        if (tempMessage) {
          tempMessage.status = 'sent';
          tempMessage.processed = true;
          tempMessage.result = action.payload.result;
        }
        state.fiasStats.messagesSent += 1;
        state.fiasStats.totalMessages += 1;
      })
      .addCase(sendFiasMessageAsync.rejected, (state, action) => {
        // Update the temp message with error status
        const tempMessage = state.fiasMessages.find(msg => msg.status === 'sending');
        if (tempMessage) {
          tempMessage.status = 'failed';
          tempMessage.error = action.payload;
          tempMessage.processed = true;
        }
        state.fiasStats.errorCount += 1;
        state.notifications.unshift({
          id: `fias_send_error_${Date.now()}`,
          type: 'error',
          message: `Failed to send FIAS message: ${action.payload}`,
          timestamp: new Date().toISOString(),
          read: false,
          category: 'fias'
        });
      });
  }
});

// ===================================
// EXPORT ACTIONS - INCLUDING MISSING ONES
// ===================================
export const {
  // Connection management
  setConnected,
  setConnecting,
  setReconnectAttempts,
  setLastError,
  setConnectionInfo,
  updateLatency,
  resetConnection,

  //  MISSING ACTIONS - NOW INCLUDED
  updateStats,
  incrementMessageCount,

  // FIAS message management
  addFiasMessage,
  updateFiasMessageStatus,
  addFiasConnection,
  removeFiasConnection,
  addBulkFiasMessages,

  // Device management
  updateDeviceStatus,
  updateBulkDeviceStatuses,
  clearDeviceEvents,

  // Room management
  updateRoomStatus,
  clearRoomEvents,

  // Voucher management
  addVoucherEvent,
  clearVoucherEvents,

  // Integration management
  updateIntegrationStatus,

  // Notification management
  addNotification,
  addAlert,
  markNotificationRead,
  markAllNotificationsRead,
  acknowledgeAlert,
  removeNotification,
  clearNotifications,
  clearAlerts,

  // System health
  updateSystemHealth,

  // Subscription management
  addSubscription,
  removeSubscription,
  updateMonitoringSettings,

  // Filter and search
  setMessageFilters,
  resetMessageFilters,

  // UI state
  updateUIState,
  toggleExpandedSection,

  // Settings
  updateSettings,
  updateSoundSettings,

  // Cleanup operations
  clearMessages,
  clearOldMessages,
  clearOldNotifications,
  resetStats,
  resetAll
} = socketSlice.actions;

// ===================================
// ENHANCED SELECTORS
// ===================================
export const selectSocket = (state) => state.socket;
export const selectConnected = (state) => state.socket.connected;
export const selectConnecting = (state) => state.socket.connecting;
export const selectConnectionInfo = (state) => state.socket.connectionInfo;
export const selectLastError = (state) => state.socket.lastError;

// FIAS selectors
export const selectFiasMessages = (state) => state.socket.fiasMessages;
export const selectFiasStats = (state) => state.socket.fiasStats;
export const selectFiasConnections = (state) => state.socket.fiasConnections;

// Device selectors
export const selectDeviceStatuses = (state) => state.socket.deviceStatuses;
export const selectDeviceEvents = (state) => state.socket.deviceEvents;

// Room selectors
export const selectRoomStatuses = (state) => state.socket.roomStatuses;
export const selectRoomEvents = (state) => state.socket.roomEvents;

// Voucher selectors
export const selectVoucherEvents = (state) => state.socket.voucherEvents;

// Notification selectors
export const selectNotifications = (state) => state.socket.notifications;
export const selectUnreadNotifications = (state) =>
  state.socket.notifications.filter(n => !n.read);
export const selectAlerts = (state) => state.socket.alerts;
export const selectUnacknowledgedAlerts = (state) =>
  state.socket.alerts.filter(a => !a.acknowledged);

// Integration selectors
export const selectIntegrationStatuses = (state) => state.socket.integrationStatuses;
export const selectIntegrationEvents = (state) => state.socket.integrationEvents;

// System selectors
export const selectSystemHealth = (state) => state.socket.systemHealth;
export const selectPerformance = (state) => state.socket.performance;

// UI selectors
export const selectUIState = (state) => state.socket.uiState;
export const selectMessageFilters = (state) => state.socket.messageFilters;
export const selectSettings = (state) => state.socket.settings;

// Subscription selectors
export const selectSubscriptions = (state) => state.socket.subscriptions;

// ===================================
// FILTERED DATA SELECTORS
// ===================================
export const selectFilteredFiasMessages = (state) => {
  const { fiasMessages, messageFilters } = state.socket;
  const { recordTypes, directions, searchTerm, dateRange, connectionId, showProcessedOnly } = messageFilters;

  return fiasMessages.filter(message => {
    // Filter by record types
    if (recordTypes.length > 0) {
      const recordId = message.message?.recordId;
      if (!recordTypes.includes(recordId)) return false;
    }

    // Filter by direction
    if (directions.length > 0 && !directions.includes(message.direction)) {
      return false;
    }

    // Filter by connection ID
    if (connectionId && message.connectionId !== connectionId) {
      return false;
    }

    // Filter by processed status
    if (showProcessedOnly && !message.processed) {
      return false;
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const messageStr = JSON.stringify(message).toLowerCase();
      if (!messageStr.includes(searchLower)) return false;
    }

    // Filter by date range
    if (dateRange.start && message.timestamp < dateRange.start) {
      return false;
    }

    if (dateRange.end && message.timestamp > dateRange.end) {
      return false;
    }

    return true;
  });
};

export const selectNotificationsByCategory = (state) => {
  const notifications = state.socket.notifications;
  return {
    all: notifications,
    connection: notifications.filter(n => n.category === 'connection'),
    fias: notifications.filter(n => n.category === 'fias'),
    device: notifications.filter(n => n.category === 'device'),
    room: notifications.filter(n => n.category === 'room'),
    voucher: notifications.filter(n => n.category === 'voucher'),
    integration: notifications.filter(n => n.category === 'integration'),
    system: notifications.filter(n => n.category === 'system')
  };
};

export const selectDeviceStatusSummary = (state) => {
  const statuses = Object.values(state.socket.deviceStatuses);
  return {
    total: statuses.length,
    online: statuses.filter(d => d.status === 'online').length,
    offline: statuses.filter(d => d.status === 'offline').length,
    error: statuses.filter(d => d.status === 'error').length,
    unknown: statuses.filter(d => d.status === 'unknown').length
  };
};

export const selectRoomStatusSummary = (state) => {
  const statuses = Object.values(state.socket.roomStatuses);
  return {
    total: statuses.length,
    occupied: statuses.filter(r => r.status === 'occupied').length,
    vacant_clean: statuses.filter(r => r.status === 'vacant_clean').length,
    vacant_dirty: statuses.filter(r => r.status === 'vacant_dirty').length,
    out_of_order: statuses.filter(r => r.status === 'out_of_order').length,
    maintenance: statuses.filter(r => r.status === 'maintenance').length
  };
};

export default socketSlice.reducer;
